import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { AdminGuard } from "../core/permissions/AdminGuard";
import { ErrorBoundary } from "../components/ui";
import NotFound from "../modules/core-pages/NotFound";
import Home from "../modules/dashboard/HomePage";
import LoginPage from "../modules/core-pages/auth/login/AuthLoginPage";
import InternalLoginPage from "../modules/core-pages/auth/login/InternalLoginPage";
import AuthCallbackPage from "../modules/core-pages/auth/callback/AuthCallbackPage";
import RegisterPage from "../modules/core-pages/auth/register/AuthRegisterPage";
import ForgotPasswordPage from "../modules/core-pages/auth/login/ForgotPasswordPage";
import ResetPasswordPage from "../modules/core-pages/auth/login/ResetPasswordPage";
import PricingPage from "../modules/core-pages/pricing/PricingPage";
import CompanyRegistrationPage from "../modules/core-pages/auth/register-company/CompanyRegistrationPage";
import { ProtectedRoute } from "../core/auth/ProtectedRoute";
import { AppShell } from "../shared/layout/AppShell";
import { RoleGuard } from "../core/permissions/RoleGuard";
import { AppRouteGuard } from "../core/permissions/AppRouteGuard";
import AccessDenied from "../modules/core-pages/AccessDenied";

// Modules
import { ProjectsPage } from "../modules/projects/list/ProjectsPage";
import ProjectsDashboardPage from "../modules/projects/pages/ProjectsDashboardPage";
import { ProjectDetailPage } from "../modules/projects/detail/ProjectDetailPage";
import { UserProfilePage as ProfilePage } from "../modules/users/profile/UserProfilePage";
import HelpCenterPage from "../modules/help/HelpCenterPage";
import SettingsPage from "../modules/settings/SettingsPage";
import { InventoryPage } from "../modules/inventory/pages/InventoryPage";
import { InventoryListPage } from "../modules/inventory/items/list/InventoryListPage";
import { EquipmentPage } from "../modules/equipment/pages/EquipmentPage";
import { UtilityPage } from "../modules/utility/pages/UtilityPage";
import { EquipmentListPage } from "../modules/equipment/list/EquipmentListPage";
import AssetsPage from "../modules/maintenance/assets/AssetsPage";
import AssetDetailPage from "../modules/maintenance/assets/AssetDetailPage";
import MaintenancePlansPage from "../modules/plan/maintenance/MaintenancePlanPage";
import MaintenanceWorkOrdersPage from "../modules/maintenance/work-orders/WorkOrdersPage";
import MaintenanceSchedulesPage from "../modules/schedules/maintenance/MaintenanceSchedulesPage";
import MaintenanceQAPage from "../modules/qa/maintenance/MaintenanceQAPage";
import MaintenancePerformancePage from "../modules/performance/maintenance/MaintenancePerformancePage";
import ZonesPage from "../modules/maintenance/zones/ZonesPage";
import MaintenanceZoneDetailPage from "../modules/maintenance/zones/ZoneDetailPage";
import ProjectZoneListPage from "../modules/projects/zones/ZoneListPage";
import ProjectZoneDetailPage from "../modules/projects/zones/ZoneDetailPage";
import ZoneNewPage from "../modules/zones/new/ZoneNewPage";
import ZoneListPage from "../modules/zones/list/ZoneListPage";
import ZonesDashboardPage from "../modules/zones/pages/ZonesDashboardPage";
import ContractsPage from "../modules/contracts/maintenance/ContractsPage";
import ContractDetailPage from "../modules/contracts/maintenance/ContractDetailPage";
import ConstructionContractsPage from "../modules/contracts/construction/ContractsPage";
import ConstructionContractDetailPage from "../modules/contracts/construction/ContractDetailPage";
import WorkloadPage from "../modules/workload/maintenance/MaintenanceWorkloadPage";
import { UsersListPage } from "../modules/users/list/UsersListPage";
import { UsersPage } from "../modules/users/pages/UsersPage";
import { UserDetailPage } from "../modules/users/detail/UserDetailPage";
import { UsersNewPage } from "../modules/users/new/UsersNewPage";
import { UsersEditPage } from "../modules/users/edit/UsersEditPage";
import TeamsListPage from "../modules/teams/list/TeamsListPage";
import TeamsDetailPage from "../modules/teams/detail/TeamsDetailPage";
import TeamsNewPage from "../modules/teams/new/TeamsNewPage";
import TeamsEditPage from "../modules/teams/edit/TeamsEditPage";

import AdminUsersPage from "../modules/admin/users/UsersPage";
import AdminUserDetailPage from "../modules/admin/users/UserDetailPage";
import CompaniesPage from "../modules/admin/companies/CompaniesPage";
import CompanyDetailPage from "../modules/admin/companies/CompanyDetailPage";
import CompanyEditPage from "../modules/admin/companies/CompanyEditPage";
import AdminUserFormPage from "../modules/admin/users/UserFormPage";
import CompanyOverviewPage from "../modules/company/CompanyOverviewPage";
import CompanySettingsPage from "../modules/company/CompanySettingsPage";
import CompanyMembersPage from "../modules/company/CompanyMembersPage";
import CompanyBillingPage from "../modules/company/CompanyBillingPage";
import CompanySecurityPage from "../modules/company/CompanySecurityPage";
import CompanyOnboardingPage from "../modules/company/CompanyOnboardingPage";

// Design Documents
import DesignDocumentListPage from "../modules/projects/design-documents/DesignDocumentListPage";
import DesignDocumentDetailPage from "../modules/projects/design-documents/DesignDocumentDetailPage";
import DocumentsListPage from "../modules/documents/DocumentsPage";
import { DocumentsPage } from "../modules/documents/pages/DocumentsPage";

// Construction Modules
import ConstructionWorkloadListPage from "../modules/workload/construction/WorkloadListPage";
import ConstructionWorkloadDetailPage from "../modules/workload/construction/WorkloadDetailPage";
import ConstructionPlanPage from "../modules/plan/construction/PlanListPage";
import ConstructionSchedulePage from "../modules/schedules/construction/ScheduleListPage";
import ConstructionProgressPage from "../modules/construction/progress/ProgressListPage";
import ConstructionProgressDetailPage from "../modules/construction/progress/ProgressDetailPage";
import ConstructionQaPage from "../modules/qa/construction/QaListPage";
import ConstructionQaDetailPage from "../modules/qa/construction/QaDetailPage";
import ConstructionInspectionsPage from "../modules/construction/inspections/InspectionListPage";
import ConstructionInspectionDetailPage from "../modules/construction/inspections/InspectionDetailPage";
import ConstructionIssuesPage from "../modules/issues/construction/IssueListPage";
import ConstructionIssueDetailPage from "../modules/issues/construction/IssueDetailPage";
import ConstructionNcrPage from "../modules/construction/ncr/NcrListPage";
import ConstructionNcrDetailPage from "../modules/construction/ncr/NcrDetailPage";
import ConstructionReworkPage from "../modules/construction/rework/ReworkListPage";
import ConstructionReworkDetailPage from "../modules/construction/rework/ReworkDetailPage";
import {
  RequestListPage as ConstructionRequestsPage,
  RequestDetailPage as ConstructionRequestDetailPage,
  RequestNewPage as ConstructionRequestNewPage,
  RequestEditPage as ConstructionRequestEditPage
} from "../modules/inventory/requests";
import ConstructionPerformanceDashboardPage from "../modules/performance/construction/PerformanceDashboardPage";
import ConstructionAutomationListPage from "../modules/automation/construction/AutomationListPage";
import RequestsPage from "../modules/requests/pages/RequestsPage";
// Equipment Modules

import { EquipmentDetailPage } from "../modules/equipment/detail/EquipmentDetailPage";
import {
  EquipmentRequestListPage as ConstructionEquipmentRequestListPage,
  EquipmentRequestNewPage as ConstructionEquipmentRequestNewPage,
  EquipmentRequestEditPage as ConstructionEquipmentRequestEditPage,
  EquipmentRequestDetailPage as ConstructionEquipmentRequestDetailPage
} from "../modules/equipment/requests";


// Utility Modules
import { UtilitySystemListPage } from "../modules/utility/list/UtilitySystemListPage";
import { UtilitySystemDetailPage } from "../modules/utility/detail/UtilitySystemDetailPage";

// Cost Tracking
import ConstructionCostTrackingPage from "../modules/cost-tracking/construction/CostTrackingPage";
import ConstructionCostTrackingDetailPage from "../modules/cost-tracking/construction/CostTrackingDetailPage";

// Payment Modules
import ConstructionPaymentClaimListPage from "../modules/construction/payments/PaymentClaimListPage";
import ConstructionPaymentClaimDetailPage from "../modules/construction/payments/PaymentClaimDetailPage";

// QS Dashboard
import ConstructionQsDashboardPage from "../modules/construction/qs/QsDashboardPage";
import BoqListPage from "../modules/construction/boq/BoqListPage";
import BoqDetailPage from "../modules/construction/boq/BoqDetailPage";
import BoqImportPage from "../modules/construction/boq/BoqImportPage";
import BoqVersionsPage from "../modules/construction/boq/BoqVersionsPage";
import BoqVariationsPage from "../modules/construction/boq/BoqVariationsPage";

// Approvals
import ApprovalsListPage from "../modules/approvals/ApprovalsListPage";
import ApprovalDetailPage from "../modules/approvals/ApprovalDetailPage";
import ApprovalsPage from "../modules/approvals/pages/ApprovalsPage";

// Notifications
import NotificationListPage from "../modules/notifications/NotificationListPage";

// Audit Logs
import AuditListPage from "../modules/audit-logs/AuditListPage";

// Reports
import ConstructionReportOverviewPage from "../modules/reports/ConstructionReportOverviewPage";

// Dashboard
import UnifiedDashboardPage from "../modules/dashboard/UnifiedDashboardPage";

// Admin Pages
import SubscriptionAdminPage from "../modules/admin/subscription/SubscriptionPage";
import AdminSettingsPage from "../modules/admin/settings/SettingsPage";


// Additional Project Pages
import { ProjectNewPage } from "../modules/projects/new/ProjectNewPage";
import { ProjectEditPage } from "../modules/projects/edit/ProjectEditPage";
import ProjectPhaseListPage from "../modules/projects/phases/PhaseListPage";
import ProjectPhaseDetailPage from "../modules/projects/phases/PhaseDetailPage";
import ConstructionSetupPage from "../modules/projects/construction-setup/ConstructionSetupPage";
import { EstimateListPage } from "../modules/estimates/list/EstimateListPage";
import EstimatesDashboardPage from "../modules/estimates/pages/EstimatesDashboardPage";
import { EstimateNewPage } from "../modules/estimates/new/EstimateNewPage";
import { EstimateDetailPage } from "../modules/estimates/detail/EstimateDetailPage";
import { NormsListPage } from "../modules/estimates/norms/NormsListPage";
import { NormDetailPage } from "../modules/estimates/norms/NormDetailPage";
import { NormNewPage } from "../modules/estimates/norms/NormNewPage";
import { ResourceRatesPage } from "../modules/estimates/rates/ResourceRatesPage";
import { ResourceFormPage } from "../modules/estimates/rates/ResourceFormPage";
import { ProcessTemplatesListPage } from "../modules/estimates/process-templates/list/ProcessTemplatesListPage";
import { ProcessTemplateDetailPage } from "../modules/estimates/process-templates/detail/ProcessTemplateDetailPage";
import { ProcessTemplateFormPage } from "../modules/estimates/process-templates/form/ProcessTemplateFormPage";

// Inventory Requests
import { InventoryDetailPage } from "../modules/inventory/items/detail/InventoryDetailPage";
import { InventoryNewPage } from "../modules/inventory/items/new/InventoryNewPage";
import { InventoryEditPage } from "../modules/inventory/items/edit/InventoryEditPage";
import { InventoryStockInPage } from "../modules/inventory/receipts/InventoryStockInPage";
import { InventoryStockOutPage } from "../modules/inventory/issues/InventoryStockOutPage";

// New Inventory Module
import { CategoriesPage as InventoryCategoriesPage } from "../modules/inventory/categories/CategoriesPage";
import { CategoriesPage as UsersCategoriesPage } from "../modules/users/categories/CategoriesPage";
import { CategoriesPage as EquipmentCategoriesPage } from "../modules/equipment/categories/CategoriesPage";
import { CategoriesPage as WorkOrdersCategoriesPage } from "../modules/work-orders/categories/CategoriesPage";
import { CategoriesPage as UtilityCategoriesPage } from "../modules/utility/categories/CategoriesPage";
import { CategoriesPage as AgroCategoriesPage } from "../modules/agro/categories/CategoriesPage";
import { UnitsPage as InventoryUnitsPage } from "../modules/inventory/units/UnitsPage";
import { WarehousesPage as InventoryWarehousesPage } from "../modules/inventory/warehouses/WarehousesPage";
import { StockMovementsPage as InventoryStockMovementsPage } from "../modules/inventory/stock-movements/StockMovementsPage";
import { ReceiptsPage as InventoryReceiptsPage } from "../modules/inventory/receipts/ReceiptsPage";
import { IssuesPage as InventoryIssuesPage } from "../modules/inventory/issues/IssuesPage";
import { TransfersPage as InventoryTransfersPage } from "../modules/inventory/transfers/TransfersPage";
import { AdjustmentsPage as InventoryAdjustmentsPage } from "../modules/inventory/adjustments/AdjustmentsPage";
import { SuppliersPage as InventorySuppliersPage } from "../modules/inventory/suppliers/SuppliersPage";
import { PriceBooksPage as InventoryPriceBooksPage } from "../modules/inventory/price-books/PriceBooksPage";
import { CostHistoryPage as InventoryCostHistoryPage } from "../modules/inventory/cost-history/CostHistoryPage";
import { LaborRatesPage as InventoryLaborRatesPage } from "../modules/inventory/labor-rates/LaborRatesPage";
import { EquipmentRatesPage as InventoryEquipmentRatesPage } from "../modules/inventory/equipment-rates/EquipmentRatesPage";
import { FuelRatesPage as InventoryFuelRatesPage } from "../modules/inventory/fuel-rates/FuelRatesPage";
import { AgroRatesPage as InventoryAgroRatesPage } from "../modules/inventory/agro-rates/AgroRatesPage";
import { EstimateResourcesPage as InventoryEstimateResourcesPage } from "../modules/inventory/estimate-resources/EstimateResourcesPage";
import { ResourceCombosPage as InventoryResourceCombosPage } from "../modules/inventory/resource-combos/ResourceCombosPage";
import { AnalyticsPage as InventoryAnalyticsPage } from "../modules/inventory/analytics/AnalyticsPage";
import { ReportsPage as InventoryReportsPage } from "../modules/inventory/reports/ReportsPage";

// Maintenance Additional Pages
import MaintenancePlanDetailPage from "../modules/plan/maintenance/MaintenancePlanDetailPage";
import MaintenanceScheduleDetailPage from "../modules/schedules/maintenance/MaintenanceScheduleDetailPage";
import MaintenanceIssueListPage from "../modules/issues/maintenance/IssueListPage";
import MaintenanceIssueDetailPage from "../modules/issues/maintenance/IssueDetailPage";
import MaintenanceLogListPage from "../modules/maintenance/logs/LogListPage";
import MaintenanceLogDetailPage from "../modules/maintenance/logs/LogDetailPage";
import MaintenanceCostListPage from "../modules/cost-tracking/maintenance/CostListPage";
import MaintenanceCostDetailPage from "../modules/cost-tracking/maintenance/CostDetailPage";
import MaintenanceQaDetailPage from "../modules/qa/maintenance/MaintenanceQaDetailPage";
import MaintenanceRouteListPage from "../modules/maintenance/routes/RouteListPage";
import MaintenanceRouteDetailPage from "../modules/maintenance/routes/RouteDetailPage";
import MaintenanceAttendancePage from "../modules/maintenance/attendance/AttendancePage";
import AttendanceShiftsPage from "../modules/maintenance/attendance/AttendanceShiftsPage";
import AssetGroupsPage from "../modules/maintenance/assets/AssetGroupsPage";
import WorkloadDetailPage from "../modules/workload/maintenance/WorkloadDetailPage";
import WorkloadRulesPage from "../modules/workload/maintenance/WorkloadRulesPage";
import WorkloadForecastPage from "../modules/workload/maintenance/WorkloadForecastPage";
import MaintenanceAutomationPage from "../modules/automation/maintenance/AutomationPage";

// Construction Additional Pages
import ConstructionScopeListPage from "../modules/construction/scope/ScopeListPage";
import ConstructionScopeDetailPage from "../modules/construction/scope/ScopeDetailPage";
import ConstructionPlanDetailPage from "../modules/plan/construction/PlanDetailPage";
import ConstructionScheduleDetailPage from "../modules/schedules/construction/ScheduleDetailPage";
import ConstructionResourceListPage from "../modules/resources/ResourceListPage";
import ConstructionDailyReportListPage from "../modules/resources/DailyReportListPage";
import ReportsListPage from "../modules/reports/ReportsPage";
import ReportsPage from "../modules/reports/pages/ReportsPage";
import AgroPage from "../modules/agro/pages/AgroPage";
import AgroRatesListPage from "../modules/agro/rates/list/AgroRatesListPage";
import { AgroProductListPage } from "../modules/agro/products/list/AgroProductListPage";

// Dashboard Role Pages
import AdminDashboardPage from "../modules/dashboard/admin/AdminDashboardPage";
import DirectorDashboardPage from "../modules/dashboard/director/DirectorDashboardPage";
import PMDashboardPage from "../modules/dashboard/pm/PMDashboardPage";
import QAQCDashboardPage from "../modules/dashboard/qa_qc/QAQCDashboardPage";
import QSDashboardPage from "../modules/dashboard/qs/QSDashboardPage";
import SiteManagerDashboardPage from "../modules/dashboard/site_manager/SiteManagerDashboardPage";
import WarehouseDashboardPage from "../modules/dashboard/warehouse/WarehouseDashboardPage";
import AccountantDashboardPage from "../modules/dashboard/accountant/AccountantDashboardPage";
import SecretaryDashboardPage from "../modules/dashboard/secretary/SecretaryDashboardPage";
import SupervisorDashboardPage from "../modules/dashboard/supervisor/SupervisorDashboardPage";
import StaffDashboardPage from "../modules/dashboard/staff/StaffDashboardPage";
import SiteForemanDashboardPage from "../modules/dashboard/site_foreman/SiteForemanDashboardPage";
import EquipmentManagerDashboardPage from "../modules/dashboard/equipment_manager/EquipmentManagerDashboardPage";
import ZoneManagerDashboardPage from "../modules/dashboard/zone_manager/ZoneManagerDashboardPage";
import AgroManagerDashboardPage from "../modules/dashboard/agro_manager/AgroManagerDashboardPage";
import ForemanDashboardPage from "../modules/dashboard/foreman/ForemanDashboardPage";
import { InventoryListPage as InventoryPageV2 } from "../modules/inventory/items/list/InventoryListPage";
import { InventoryDetailPage as InventoryPageDetailV2 } from "../modules/inventory/items/detail/InventoryDetailPage";

import WorkOrdersPageV2 from "../modules/work-orders/page/WorkOrdersPage";
import WorkOrdersDashboardPage from "../modules/work-orders/pages/WorkOrdersDashboardPage";
import WorkOrderCreatePageV2 from "../modules/work-orders/new/WorkOrderNewPage";
import WorkOrdersDetailPageV2 from "../modules/work-orders/detail/WorkOrdersDetailPage";
import WorkOrderEditPageV2 from "../modules/work-orders/edit/WorkOrderEditPage";
import { SubTasksListPage } from "../modules/work-orders/sub-tasks/list/SubTasksListPage";
import SubTaskNewPage from "../modules/work-orders/sub-tasks/new/SubTaskNewPage";
import SubTaskEditPage from "../modules/work-orders/sub-tasks/edit/SubTaskEditPage";
import SubTaskDetailPage from "../modules/work-orders/sub-tasks/detail/SubTaskDetailPage";
import { DocsViewerPage } from "../modules/docs/DocsViewerPage";

function ProtectedShell({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppRouteGuard>
        <AppShell>
          <ErrorBoundary>{children}</ErrorBoundary>
        </AppShell>
      </AppRouteGuard>
    </ProtectedRoute>
  );
}

const routes: RouteObject[] = [
  { path: "/test-inventory", element: <ProtectedShell><InventoryPageV2 /></ProtectedShell> },
  { path: "/test-inventory-detail", element: <ProtectedShell><InventoryPageDetailV2 /></ProtectedShell> },
  { path: "/test", element: <ProtectedShell><ForemanDashboardPage /></ProtectedShell> },
  { path: "/test1", element: <ProtectedShell><AdminDashboardPage /></ProtectedShell> },
  { path: "/test2", element: <ProtectedShell><DirectorDashboardPage /></ProtectedShell> },
  { path: "/test3", element: <ProtectedShell><PMDashboardPage /></ProtectedShell> },
  { path: "/test4", element: <ProtectedShell><QAQCDashboardPage /></ProtectedShell> },
  { path: "/test5", element: <ProtectedShell><QSDashboardPage /></ProtectedShell> },
  { path: "/test6", element: <ProtectedShell><SiteManagerDashboardPage /></ProtectedShell> },
  { path: "/test7", element: <ProtectedShell><WarehouseDashboardPage /></ProtectedShell> },
  { path: "/test8", element: <ProtectedShell><AccountantDashboardPage /></ProtectedShell> },
  { path: "/test9", element: <ProtectedShell><SecretaryDashboardPage /></ProtectedShell> },
  { path: "/test10", element: <ProtectedShell><SupervisorDashboardPage /></ProtectedShell> },
  { path: "/test11", element: <ProtectedShell><StaffDashboardPage /></ProtectedShell> },
  { path: "/test12", element: <ProtectedShell><SiteForemanDashboardPage /></ProtectedShell> },
  { path: "/test13", element: <ProtectedShell><EquipmentManagerDashboardPage /></ProtectedShell> },
  { path: "/test14", element: <ProtectedShell><ZoneManagerDashboardPage /></ProtectedShell> },
  { path: "/test15", element: <ProtectedShell><AgroManagerDashboardPage /></ProtectedShell> },
  { path: "/auth/login", element: <Navigate to="/auth/internal-login" replace /> },
  { path: "/auth/internal-login", element: <InternalLoginPage /> },
  { path: "/auth/register", element: <RegisterPage /> },
  { path: "/auth/register-company", element: <CompanyRegistrationPage /> },
  { path: "/auth/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/auth/reset-password", element: <ResetPasswordPage /> },
  { path: "/pricing", element: <PricingPage /> },
  { path: "/auth/callback", element: <AuthCallbackPage /> },
  { path: "/access-denied", element: <AccessDenied /> },
  { path: "/", element: <ProtectedShell><UnifiedDashboardPage /></ProtectedShell> },
  { path: "/dashboard", element: <ProtectedShell><UnifiedDashboardPage /></ProtectedShell> },
  { path: "/foreman-dashboard", element: <ProtectedShell><ForemanDashboardPage /></ProtectedShell> },
  { path: "/projects", element: <ProtectedShell><ProjectsDashboardPage /></ProtectedShell> },
  { path: "/projects/list", element: <ProtectedShell><ProjectsPage /></ProtectedShell> },
  { path: "/documents", element: <ProtectedShell><DocumentsPage /></ProtectedShell> },
  { path: "/documents/list", element: <ProtectedShell><DocumentsListPage /></ProtectedShell> },
  { path: "/docs", element: <ProtectedShell><DocsViewerPage /></ProtectedShell> },
  { path: "/docs/:docId", element: <ProtectedShell><DocsViewerPage /></ProtectedShell> },
  { path: "/projects/:id", element: <ProtectedShell><ProjectDetailPage /></ProtectedShell> },
  { path: "/estimates", element: <ProtectedShell><EstimatesDashboardPage /></ProtectedShell> },
  { path: "/estimates/list", element: <ProtectedShell><EstimateListPage /></ProtectedShell> },
  { path: "/estimates/new", element: <ProtectedShell><EstimateNewPage /></ProtectedShell> },
  { path: "/estimates/process-templates", element: <ProtectedShell><ProcessTemplatesListPage /></ProtectedShell> },
  { path: "/estimates/process-templates/new", element: <ProtectedShell><ProcessTemplateFormPage mode="new" /></ProtectedShell> },
  { path: "/estimates/process-templates/:id", element: <ProtectedShell><ProcessTemplateDetailPage /></ProtectedShell> },
  { path: "/estimates/process-templates/:id/edit", element: <ProtectedShell><ProcessTemplateFormPage mode="edit" /></ProtectedShell> },
  { path: "/estimates/norms", element: <ProtectedShell><NormsListPage /></ProtectedShell> },
  { path: "/estimates/norms/new", element: <ProtectedShell><NormNewPage /></ProtectedShell> },
  { path: "/estimates/norms/:id", element: <ProtectedShell><NormDetailPage /></ProtectedShell> },
  { path: "/estimates/rates", element: <ProtectedShell><ResourceRatesPage /></ProtectedShell> },
  { path: "/estimates/rates/form", element: <ProtectedShell><ResourceFormPage /></ProtectedShell> },
  { path: "/estimates/:id", element: <ProtectedShell><EstimateDetailPage /></ProtectedShell> },
  { path: "/admin/companies", element: <ProtectedShell><RoleGuard allowedRoles={['super_admin']}><CompaniesPage /></RoleGuard></ProtectedShell> },
  { path: "/admin/companies/detail/:id", element: <ProtectedShell><RoleGuard allowedRoles={['super_admin']}><CompanyDetailPage /></RoleGuard></ProtectedShell> },
  { path: "/admin/companies/edit/:id", element: <ProtectedShell><RoleGuard allowedRoles={['super_admin']}><CompanyEditPage /></RoleGuard></ProtectedShell> },
  { path: "/admin/users", element: <ProtectedShell><RoleGuard allowedRoles={['super_admin']}><AdminUsersPage /></RoleGuard></ProtectedShell> },
  { path: "/admin/users/new", element: <ProtectedShell><RoleGuard allowedRoles={['super_admin']}><AdminUserFormPage /></RoleGuard></ProtectedShell> },
  { path: "/admin/users/detail/:id", element: <ProtectedShell><RoleGuard allowedRoles={['super_admin']}><AdminUserDetailPage /></RoleGuard></ProtectedShell> },
  { path: "/admin/users/edit/:id", element: <ProtectedShell><RoleGuard allowedRoles={['super_admin']}><AdminUserFormPage /></RoleGuard></ProtectedShell> },
  { path: "/company", element: <ProtectedShell><CompanyOverviewPage /></ProtectedShell> },
  { path: "/company/settings", element: <ProtectedShell><CompanySettingsPage /></ProtectedShell> },
  { path: "/company/members", element: <ProtectedShell><CompanyMembersPage /></ProtectedShell> },
  { path: "/company/billing", element: <ProtectedShell><CompanyBillingPage /></ProtectedShell> },
  { path: "/company/security", element: <ProtectedShell><CompanySecurityPage /></ProtectedShell> },
  { path: "/company/onboarding", element: <ProtectedShell><CompanyOnboardingPage /></ProtectedShell> },
  { path: "/profile", element: <ProtectedShell><ProfilePage /></ProtectedShell> },
  { path: "/help", element: <ProtectedShell><HelpCenterPage /></ProtectedShell> },
  { path: "/company/subscription", element: <Navigate to="/settings" replace /> },
  { path: "/settings", element: <ProtectedShell><SettingsPage /></ProtectedShell> },
  // { path: "/inventory", element: <Navigate to="/inventory/items" replace /> },
  { path: "/inventory/items/new", element: <ProtectedShell><InventoryNewPage /></ProtectedShell> },
  { path: "/equipment", element: <ProtectedShell><EquipmentPage /></ProtectedShell> },
  { path: "/construction/contracts", element: <ProtectedShell><ConstructionContractsPage /></ProtectedShell> },
  { path: "/construction/contracts/detail/:id", element: <ProtectedShell><ConstructionContractDetailPage /></ProtectedShell> },
  { path: "/construction/boq", element: <ProtectedShell><BoqListPage /></ProtectedShell> },
  { path: "/construction/boq/detail/:id", element: <ProtectedShell><BoqDetailPage /></ProtectedShell> },
  { path: "/construction/boq/import", element: <ProtectedShell><BoqImportPage /></ProtectedShell> },
  { path: "/construction/boq/versions/:id", element: <ProtectedShell><BoqVersionsPage /></ProtectedShell> },
  { path: "/construction/boq/variations/:id", element: <ProtectedShell><BoqVariationsPage /></ProtectedShell> },
  { path: "/maintenance/contracts", element: <ProtectedShell><ContractsPage defaultType="maintenance" /></ProtectedShell> },
  { path: "/maintenance/contracts/detail/:id", element: <ProtectedShell><ContractDetailPage /></ProtectedShell> },
  { path: "/maintenance/assets", element: <ProtectedShell><AssetsPage /></ProtectedShell> },
  { path: "/maintenance/assets/detail/:id", element: <ProtectedShell><AssetDetailPage /></ProtectedShell> },
  { path: "/maintenance/:projectId/assets", element: <ProtectedShell><AssetsPage /></ProtectedShell> },
  { path: "/maintenance/:projectId/assets/:id", element: <ProtectedShell><AssetDetailPage /></ProtectedShell> },
  { path: "/maintenance/:projectId/plans", element: <ProtectedShell><MaintenancePlansPage /></ProtectedShell> },
  { path: "/maintenance/:projectId/schedules", element: <ProtectedShell><MaintenanceSchedulesPage /></ProtectedShell> },
  { path: "/maintenance/:projectId/zones", element: <ProtectedShell><ZonesPage /></ProtectedShell> },
  { path: "/maintenance/:projectId/qa", element: <ProtectedShell><MaintenanceQAPage /></ProtectedShell> },
  {
    path: "/maintenance/zones",
    element: (
      <ProtectedShell>
        <ZonesPage />
      </ProtectedShell>
    ),
  },
  { path: "/maintenance/zones/detail/:id", element: <ProtectedShell><MaintenanceZoneDetailPage /></ProtectedShell> },
  { path: "/zones", element: <ProtectedShell><ZonesDashboardPage /></ProtectedShell> },
  { path: "/zones/list", element: <ProtectedShell><ZoneListPage /></ProtectedShell> },
  { path: "/zones/new", element: <ProtectedShell><ZoneNewPage /></ProtectedShell> },
  { path: "/projects/zones/detail/:id", element: <ProtectedShell><ProjectZoneDetailPage /></ProtectedShell> },
  { path: "/maintenance/:projectId/zones/:id", element: <ProtectedShell><ProjectZoneDetailPage /></ProtectedShell> },
  { path: "/maintenance/:projectId/contracts/:id", element: <ProtectedShell><ContractDetailPage /></ProtectedShell> },
  { path: "/maintenance/:projectId/workload", element: <ProtectedShell><WorkloadPage /></ProtectedShell> },
  { path: "/teams", element: <ProtectedShell><TeamsListPage /></ProtectedShell> },
  { path: "/teams/new", element: <ProtectedShell><TeamsNewPage /></ProtectedShell> },
  { path: "/teams/:id", element: <ProtectedShell><TeamsDetailPage /></ProtectedShell> },
  { path: "/teams/edit/:id", element: <ProtectedShell><TeamsEditPage /></ProtectedShell> },
  { path: "/maintenance/teams", element: <Navigate to="/teams" replace /> },
  { path: "/construction/teams", element: <Navigate to="/teams" replace /> },
  {
    path: "/utility",
    element: (
      <ProtectedShell>
        <RoleGuard allowedRoles={['admin', 'equipment_manager', 'supervisor', 'site_manager', 'director', 'pm']}>
          <UtilityPage />
        </RoleGuard>
      </ProtectedShell>
    ),
  },
  {
    path: "/users",
    element: (
      <ProtectedShell>
        <RoleGuard allowedRoles={['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'qs', 'site_manager', 'warehouse', 'accountant', 'secretary', 'supervisor', 'staff', 'site_foreman', 'equipment_manager', 'zone_manager', 'agro_manager']}>
          <UsersPage />
        </RoleGuard>
      </ProtectedShell>
    ),
  },
  {
    path: "/users/list",
    element: (
      <ProtectedShell>
        <RoleGuard allowedRoles={['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'qs', 'site_manager', 'warehouse', 'accountant', 'secretary', 'supervisor', 'staff', 'site_foreman', 'equipment_manager', 'zone_manager', 'agro_manager']}>
          <UsersListPage />
        </RoleGuard>
      </ProtectedShell>
    ),
  },
  {
    path: "/users/:id",
    element: (
      <ProtectedShell>
        <RoleGuard allowedRoles={['super_admin', 'admin', 'director', 'pm', 'qa_qc', 'qs', 'site_manager', 'warehouse', 'accountant', 'secretary', 'supervisor', 'staff', 'site_foreman', 'equipment_manager', 'zone_manager', 'agro_manager']}>
          <UserDetailPage />
        </RoleGuard>
      </ProtectedShell>
    ),
  },
  {
    path: "/users/new",
    element: (
      <ProtectedShell>
        <RoleGuard allowedRoles={['super_admin', 'admin', 'director', 'pm']}>
          <UsersNewPage />
        </RoleGuard>
      </ProtectedShell>
    ),
  },
  {
    path: "/users/:id/edit",
    element: (
      <ProtectedShell>
        <RoleGuard allowedRoles={['super_admin', 'admin', 'director', 'pm']}>
          <UsersEditPage />
        </RoleGuard>
      </ProtectedShell>
    ),
  },
  {
    path: "/admin",
    element: (
      <ProtectedShell>
        <RoleGuard allowedRoles={['super_admin']}>
          <AdminDashboardPage />
        </RoleGuard>
      </ProtectedShell>
    ),
  },
  {
    path: "/admin/subscription",
    element: (
      <ProtectedShell>
        <RoleGuard allowedRoles={['super_admin']}>
          <SubscriptionAdminPage />
        </RoleGuard>
      </ProtectedShell>
    ),
  },
  {
    path: "/admin/settings",
    element: (
      <ProtectedShell>
        <RoleGuard allowedRoles={['super_admin']}>
          <AdminSettingsPage />
        </RoleGuard>
      </ProtectedShell>
    ),
  },

  // Project Design Documents
  { path: "/projects/:projectId/design-documents", element: <ProtectedShell><DesignDocumentListPage /></ProtectedShell> },
  { path: "/projects/:projectId/design-documents/detail/:id", element: <ProtectedShell><DesignDocumentDetailPage /></ProtectedShell> },

  // Construction Domain
  { path: "/construction/:projectId/workload", element: <ProtectedShell><ConstructionWorkloadListPage /></ProtectedShell> },
  { path: "/construction/:projectId/workload/detail/:id", element: <ProtectedShell><ConstructionWorkloadDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/teams", element: <Navigate to="/teams" replace /> },
  { path: "/construction/:projectId/plan", element: <ProtectedShell><ConstructionPlanPage /></ProtectedShell> },
  { path: "/construction/:projectId/schedule", element: <ProtectedShell><ConstructionSchedulePage /></ProtectedShell> },
  { path: "/construction/:projectId/progress", element: <ProtectedShell><ConstructionProgressPage /></ProtectedShell> },
  { path: "/construction/:projectId/progress/detail/:id", element: <ProtectedShell><ConstructionProgressDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/qa", element: <ProtectedShell><ConstructionQaPage /></ProtectedShell> },
  { path: "/construction/:projectId/qa/detail/:id", element: <ProtectedShell><ConstructionQaDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/inspections", element: <ProtectedShell><ConstructionInspectionsPage /></ProtectedShell> },
  { path: "/construction/:projectId/inspections/detail/:id", element: <ProtectedShell><ConstructionInspectionDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/issues", element: <ProtectedShell><ConstructionIssuesPage /></ProtectedShell> },
  { path: "/construction/:projectId/issues/detail/:id", element: <ProtectedShell><ConstructionIssueDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/ncr", element: <ProtectedShell><ConstructionNcrPage /></ProtectedShell> },
  { path: "/construction/:projectId/ncr/detail/:id", element: <ProtectedShell><ConstructionNcrDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/rework", element: <ProtectedShell><ConstructionReworkPage /></ProtectedShell> },
  { path: "/construction/:projectId/rework/detail/:id", element: <ProtectedShell><ConstructionReworkDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/requests", element: <ProtectedShell><ConstructionRequestsPage /></ProtectedShell> },
  { path: "/construction/:projectId/requests/detail/:id", element: <ProtectedShell><ConstructionRequestDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/performance", element: <ProtectedShell><ConstructionPerformanceDashboardPage /></ProtectedShell> },
  { path: "/construction/:projectId/automation", element: <ProtectedShell><ConstructionAutomationListPage /></ProtectedShell> },

  // Equipment Domain
  { path: "/equipment/list", element: <ProtectedShell><EquipmentListPage /></ProtectedShell> },
  { path: "/equipment/detail/:id", element: <ProtectedShell><EquipmentDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/equipment-requests", element: <ProtectedShell><ConstructionEquipmentRequestListPage /></ProtectedShell> },
  { path: "/construction/:projectId/equipment-requests/new", element: <ProtectedShell><ConstructionEquipmentRequestNewPage /></ProtectedShell> },
  { path: "/construction/:projectId/equipment-requests/detail/:id", element: <ProtectedShell><ConstructionEquipmentRequestDetailPage /></ProtectedShell> },
  { path: "/construction/:projectId/equipment-requests/edit/:id", element: <ProtectedShell><ConstructionEquipmentRequestEditPage /></ProtectedShell> },

  // Utility Domain
  { path: "/utility", element: <ProtectedShell><UtilityPage /></ProtectedShell> },
  { path: "/utility/systems", element: <ProtectedShell><UtilitySystemListPage /></ProtectedShell> },
  { path: "/utility/systems/detail/:id", element: <ProtectedShell><UtilitySystemDetailPage /></ProtectedShell> },

  // Cost Tracking
  { path: "/construction/:projectId/cost-tracking", element: <ProtectedShell><ConstructionCostTrackingPage /></ProtectedShell> },
  { path: "/construction/:projectId/cost-tracking/detail/:id", element: <ProtectedShell><ConstructionCostTrackingDetailPage /></ProtectedShell> },

  // Payment Domain
  { path: "/construction/:projectId/payments", element: <ProtectedShell><ConstructionPaymentClaimListPage /></ProtectedShell> },
  { path: "/construction/:projectId/payments/detail/:id", element: <ProtectedShell><ConstructionPaymentClaimDetailPage /></ProtectedShell> },

  // QS Domain
  { path: "/construction/:projectId/qs-dashboard", element: <ProtectedShell><ConstructionQsDashboardPage /></ProtectedShell> },

  // Approvals Domain
  { path: "/approvals", element: <ProtectedShell><ApprovalsPage /></ProtectedShell> },
  { path: "/approvals/list", element: <ProtectedShell><ApprovalsListPage /></ProtectedShell> },
  { path: "/approvals/detail/:id", element: <ProtectedShell><ApprovalDetailPage /></ProtectedShell> },

  // Notifications Domain
  { path: "/notifications", element: <ProtectedShell><NotificationListPage /></ProtectedShell> },

  // Audit Logs Domain
  { path: "/audit-logs", element: <ProtectedShell><AuditListPage /></ProtectedShell> },

  // Reports Domain
  { path: "/reports/construction", element: <ProtectedShell><ConstructionReportOverviewPage /></ProtectedShell> },

  // Route aliases used by role landing pages and quick actions

  { path: "/plant-intelligence", element: <Navigate to="/maintenance/assets" replace /> },
  { path: "/progress", element: <Navigate to="/construction/progress" replace /> },
  { path: "/payments", element: <Navigate to="/construction/payments" replace /> },
  { path: "/quality", element: <Navigate to="/construction/qa" replace /> },
  { path: "/export", element: <Navigate to="/reports/construction" replace /> },

  // Project CRUD and setup
  { path: "/projects/new", element: <ProtectedShell><ProjectNewPage /></ProtectedShell> },
  { path: "/projects/:id/edit", element: <ProtectedShell><ProjectEditPage /></ProtectedShell> },
  { path: "/projects/:projectId/zones", element: <ProtectedShell><ProjectZoneListPage /></ProtectedShell> },
  { path: "/projects/:projectId/zones/detail/:id", element: <ProtectedShell><ProjectZoneDetailPage /></ProtectedShell> },
  { path: "/projects/:projectId/phases", element: <ProtectedShell><ProjectPhaseListPage /></ProtectedShell> },
  { path: "/projects/:projectId/phases/detail/:id", element: <ProtectedShell><ProjectPhaseDetailPage /></ProtectedShell> },
  { path: "/projects/:projectId/construction-setup", element: <ProtectedShell><ConstructionSetupPage /></ProtectedShell> },

  // Inventory and material requests
  { path: "/inventory/items/detail/:id", element: <ProtectedShell><InventoryDetailPage /></ProtectedShell> },
  { path: "/inventory/items/edit/:id", element: <ProtectedShell><InventoryEditPage /></ProtectedShell> },
  { path: "/inventory/stock-in/:id", element: <ProtectedShell><InventoryStockInPage /></ProtectedShell> },
  { path: "/inventory/stock-out/:id", element: <ProtectedShell><InventoryStockOutPage /></ProtectedShell> },

  // New Inventory Routes
  { path: "/inventory", element: <ProtectedShell><InventoryPage /></ProtectedShell> },
  { path: "/inventory/items", element: <ProtectedShell><InventoryListPage /></ProtectedShell> },
  { path: "/inventory/categories", element: <ProtectedShell><InventoryCategoriesPage /></ProtectedShell> },
  { path: "/users/categories", element: <ProtectedShell><UsersCategoriesPage /></ProtectedShell> },
  { path: "/equipment/categories", element: <ProtectedShell><EquipmentCategoriesPage /></ProtectedShell> },
  { path: "/work-orders/categories", element: <ProtectedShell><WorkOrdersCategoriesPage /></ProtectedShell> },
  { path: "/utility/categories", element: <ProtectedShell><UtilityCategoriesPage /></ProtectedShell> },
  { path: "/agro/categories", element: <ProtectedShell><AgroCategoriesPage /></ProtectedShell> },
  { path: "/inventory/units", element: <ProtectedShell><InventoryUnitsPage /></ProtectedShell> },
  { path: "/inventory/warehouses", element: <ProtectedShell><InventoryWarehousesPage /></ProtectedShell> },
  { path: "/inventory/stock-movements", element: <ProtectedShell><InventoryStockMovementsPage /></ProtectedShell> },
  { path: "/inventory/receipts", element: <ProtectedShell><InventoryReceiptsPage /></ProtectedShell> },
  { path: "/inventory/issues", element: <ProtectedShell><InventoryIssuesPage /></ProtectedShell> },
  { path: "/inventory/transfers", element: <ProtectedShell><InventoryTransfersPage /></ProtectedShell> },
  { path: "/inventory/adjustments", element: <ProtectedShell><InventoryAdjustmentsPage /></ProtectedShell> },
  { path: "/inventory/suppliers", element: <ProtectedShell><InventorySuppliersPage /></ProtectedShell> },
  { path: "/inventory/price-books", element: <ProtectedShell><InventoryPriceBooksPage /></ProtectedShell> },
  { path: "/inventory/cost-history", element: <ProtectedShell><InventoryCostHistoryPage /></ProtectedShell> },
  { path: "/inventory/labor-rates", element: <ProtectedShell><InventoryLaborRatesPage /></ProtectedShell> },
  { path: "/inventory/equipment-rates", element: <ProtectedShell><InventoryEquipmentRatesPage /></ProtectedShell> },
  { path: "/inventory/fuel-rates", element: <ProtectedShell><InventoryFuelRatesPage /></ProtectedShell> },
  { path: "/inventory/agro-rates", element: <ProtectedShell><InventoryAgroRatesPage /></ProtectedShell> },
  { path: "/inventory/estimate-resources", element: <ProtectedShell><InventoryEstimateResourcesPage /></ProtectedShell> },
  { path: "/inventory/resource-combos", element: <ProtectedShell><InventoryResourceCombosPage /></ProtectedShell> },
  { path: "/inventory/analytics", element: <ProtectedShell><InventoryAnalyticsPage /></ProtectedShell> },
  { path: "/inventory/reports", element: <ProtectedShell><InventoryReportsPage /></ProtectedShell> },
  { path: "/requests/new", element: <ProtectedShell><ConstructionRequestNewPage /></ProtectedShell> },
  { path: "/requests/detail/:id", element: <ProtectedShell><ConstructionRequestDetailPage /></ProtectedShell> },
  { path: "/requests/edit/:id", element: <ProtectedShell><ConstructionRequestEditPage /></ProtectedShell> },

  // Maintenance global routes
  { path: "/maintenance/plans", element: <ProtectedShell><MaintenancePlansPage /></ProtectedShell> },
  { path: "/maintenance/plans/detail/:id", element: <ProtectedShell><MaintenancePlanDetailPage /></ProtectedShell> },

  { path: "/maintenance/schedules", element: <ProtectedShell><MaintenanceSchedulesPage /></ProtectedShell> },
  { path: "/maintenance/schedules/detail/:id", element: <ProtectedShell><MaintenanceScheduleDetailPage /></ProtectedShell> },
  { path: "/maintenance/qa", element: <ProtectedShell><MaintenanceQAPage /></ProtectedShell> },
  { path: "/maintenance/qa/detail/:id", element: <ProtectedShell><MaintenanceQaDetailPage /></ProtectedShell> },
  { path: "/maintenance/issues", element: <ProtectedShell><MaintenanceIssueListPage /></ProtectedShell> },
  { path: "/maintenance/issues/detail/:id", element: <ProtectedShell><MaintenanceIssueDetailPage /></ProtectedShell> },
  { path: "/maintenance/logs", element: <ProtectedShell><MaintenanceLogListPage /></ProtectedShell> },
  { path: "/maintenance/logs/detail/:id", element: <ProtectedShell><MaintenanceLogDetailPage /></ProtectedShell> },
  { path: "/maintenance/cost-tracking", element: <ProtectedShell><MaintenanceCostListPage /></ProtectedShell> },
  { path: "/maintenance/cost-tracking/detail/:id", element: <ProtectedShell><MaintenanceCostDetailPage /></ProtectedShell> },
  { path: "/maintenance/routes", element: <ProtectedShell><MaintenanceRouteListPage /></ProtectedShell> },
  { path: "/maintenance/routes/detail/:id", element: <ProtectedShell><MaintenanceRouteDetailPage /></ProtectedShell> },
  { path: "/maintenance/attendance", element: <ProtectedShell><MaintenanceAttendancePage /></ProtectedShell> },
  { path: "/maintenance/attendance/shifts", element: <ProtectedShell><AttendanceShiftsPage /></ProtectedShell> },
  { path: "/maintenance/assets/groups", element: <ProtectedShell><AssetGroupsPage /></ProtectedShell> },
  { path: "/maintenance/workload", element: <ProtectedShell><WorkloadPage /></ProtectedShell> },
  { path: "/maintenance/workload/detail/:id", element: <ProtectedShell><WorkloadDetailPage /></ProtectedShell> },
  { path: "/maintenance/workload/rules", element: <ProtectedShell><WorkloadRulesPage /></ProtectedShell> },
  { path: "/maintenance/workload/forecast", element: <ProtectedShell><WorkloadForecastPage /></ProtectedShell> },
  { path: "/maintenance/teams", element: <Navigate to="/teams" replace /> },
  { path: "/maintenance/performance", element: <ProtectedShell><MaintenancePerformancePage /></ProtectedShell> },
  { path: "/maintenance/automation", element: <ProtectedShell><MaintenanceAutomationPage /></ProtectedShell> },

  // Construction global routes
  { path: "/construction/boq", element: <ProtectedShell><BoqListPage /></ProtectedShell> },
  { path: "/construction/boq/import", element: <ProtectedShell><BoqImportPage /></ProtectedShell> },
  { path: "/construction/boq/:id", element: <ProtectedShell><BoqDetailPage /></ProtectedShell> },
  { path: "/construction/boq/:id/versions", element: <ProtectedShell><BoqVersionsPage /></ProtectedShell> },
  { path: "/construction/boq/:id/variations", element: <ProtectedShell><BoqVariationsPage /></ProtectedShell> },

  { path: "/construction/workload/detail/:id", element: <ProtectedShell><ConstructionWorkloadDetailPage /></ProtectedShell> },
  { path: "/construction/teams", element: <Navigate to="/teams" replace /> },
  { path: "/construction/plan", element: <ProtectedShell><ConstructionPlanPage /></ProtectedShell> },
  { path: "/construction/plan/detail/:id", element: <ProtectedShell><ConstructionPlanDetailPage /></ProtectedShell> },
  { path: "/construction/schedules", element: <ProtectedShell><ConstructionSchedulePage /></ProtectedShell> },
  { path: "/construction/schedules/detail/:id", element: <ProtectedShell><ConstructionScheduleDetailPage /></ProtectedShell> },
  { path: "/construction/schedule", element: <Navigate to="/construction/schedules" replace /> },
  { path: "/work-orders", element: <ProtectedShell><WorkOrdersDashboardPage /></ProtectedShell> },
  { path: "/work-orders/list", element: <ProtectedShell><WorkOrdersPageV2 /></ProtectedShell> },
  { path: "/work-orders/new", element: <ProtectedShell><WorkOrderCreatePageV2 /></ProtectedShell> },
  { path: "/work-orders/detail/:id", element: <ProtectedShell><WorkOrdersDetailPageV2 /></ProtectedShell> },
  { path: "/work-orders/detail/:id/sub-tasks", element: <ProtectedShell><SubTasksListPage /></ProtectedShell> },
  { path: "/work-orders/detail/:id/sub-tasks/new", element: <ProtectedShell><SubTaskNewPage /></ProtectedShell> },
  { path: "/work-orders/sub-tasks/:subTaskId", element: <ProtectedShell><SubTaskDetailPage /></ProtectedShell> },
  { path: "/work-orders/sub-tasks/edit/:subTaskId", element: <ProtectedShell><SubTaskEditPage /></ProtectedShell> },
  { path: "/work-orders/edit/:id", element: <ProtectedShell><WorkOrderEditPageV2 /></ProtectedShell> },
  { path: "/construction/progress", element: <ProtectedShell><ConstructionProgressPage /></ProtectedShell> },
  { path: "/construction/progress/detail/:id", element: <ProtectedShell><ConstructionProgressDetailPage /></ProtectedShell> },
  { path: "/construction/qa", element: <ProtectedShell><ConstructionQaPage /></ProtectedShell> },
  { path: "/construction/qa/detail/:id", element: <ProtectedShell><ConstructionQaDetailPage /></ProtectedShell> },
  { path: "/construction/inspections", element: <ProtectedShell><ConstructionInspectionsPage /></ProtectedShell> },
  { path: "/construction/inspections/detail/:id", element: <ProtectedShell><ConstructionInspectionDetailPage /></ProtectedShell> },
  { path: "/construction/issues", element: <ProtectedShell><ConstructionIssuesPage /></ProtectedShell> },
  { path: "/construction/issues/detail/:id", element: <ProtectedShell><ConstructionIssueDetailPage /></ProtectedShell> },
  { path: "/construction/ncr", element: <ProtectedShell><ConstructionNcrPage /></ProtectedShell> },
  { path: "/construction/ncr/detail/:id", element: <ProtectedShell><ConstructionNcrDetailPage /></ProtectedShell> },
  { path: "/construction/rework", element: <ProtectedShell><ConstructionReworkPage /></ProtectedShell> },
  { path: "/construction/rework/detail/:id", element: <ProtectedShell><ConstructionReworkDetailPage /></ProtectedShell> },
  { path: "/construction/requests", element: <ProtectedShell><ConstructionRequestsPage /></ProtectedShell> },
  { path: "/requests", element: <ProtectedShell><RequestsPage /></ProtectedShell> },
  { path: "/requests/list", element: <ProtectedShell><ConstructionRequestsPage /></ProtectedShell> },
  { path: "/construction/requests/detail/:id", element: <ProtectedShell><ConstructionRequestDetailPage /></ProtectedShell> },
  { path: "/construction/performance", element: <ProtectedShell><ConstructionPerformanceDashboardPage /></ProtectedShell> },
  { path: "/construction/automation", element: <ProtectedShell><ConstructionAutomationListPage /></ProtectedShell> },
  { path: "/construction/cost-tracking", element: <ProtectedShell><ConstructionCostTrackingPage /></ProtectedShell> },
  { path: "/construction/cost-tracking/detail/:id", element: <ProtectedShell><ConstructionCostTrackingDetailPage /></ProtectedShell> },
  { path: "/construction/payments", element: <ProtectedShell><ConstructionPaymentClaimListPage /></ProtectedShell> },
  { path: "/construction/payments/detail/:id", element: <ProtectedShell><ConstructionPaymentClaimDetailPage /></ProtectedShell> },
  { path: "/construction/qs-dashboard", element: <ProtectedShell><ConstructionQsDashboardPage /></ProtectedShell> },
  { path: "/construction/scope", element: <ProtectedShell><ConstructionScopeListPage /></ProtectedShell> },
  { path: "/construction/scope/detail/:id", element: <ProtectedShell><ConstructionScopeDetailPage /></ProtectedShell> },
  { path: "/resources", element: <ProtectedShell><ConstructionResourceListPage /></ProtectedShell> },
  { path: "/construction/daily-reports", element: <ProtectedShell><ConstructionDailyReportListPage /></ProtectedShell> },

  // Reports and resource aliases
  { path: "/reports", element: <ProtectedShell><ReportsPage /></ProtectedShell> },
  { path: "/reports/list", element: <ProtectedShell><ReportsListPage /></ProtectedShell> },
  { path: "/agro", element: <ProtectedShell><AgroPage /></ProtectedShell> },
  { path: "/agro/rates", element: <ProtectedShell><AgroRatesListPage /></ProtectedShell> },
  { path: "/agro/products", element: <ProtectedShell><AgroProductListPage /></ProtectedShell> },

  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
